import { apiEndpoints, AmorgAdminHttpCommandResult, AppendixFieldChange, ArticleChangeImportance, ArticleContact, ArticleFieldChange, ArticleVersionList, ReleaseFollowUp, RetsinfoHarvestRecord } from '../amorg-common';
import { ArticleList, ArticlePage, ArticleParameters, ArticleTopicSubscriberListing, ArticleTypeListing } from '../amorg-common';

const apiPath = '/api';
const articlesPath = `${apiPath}/amorgy/articles`;

const onNoAccess = () =>
{
    document.location.assign('/amorgy/login');
};

export const articlesClientApi =
{
    getTypes,
    createType,
    updateType,
    deleteType,
    getLists,
    createList,
    updateList,
    deleteList,
    createListTopic,
    updateListTopic,
    deleteListTopic,
    getArticleListing,
    getArticleVersions,
    createArticle,
    createArticleVersion,
    updateArticleField,
    createNote,
    updateNote,
    deleteNote,
    createAppendix,
    updateAppendixField,
    deleteAppendix,
    releaseArticleVersion,
    mergeUnreleasedArticleVersion,
    updateReleaseRecord,
    getSubscriberListings,
    createSubscriber,
    toggleArticleSubscription,
    getFollowups,
    getContacts,
    getRetsinfoRecords,
    fetchRetsinfoRecords,
    fetchRetsinfoRecordChangeDoc,
    updateRetsinfoRecordFlag
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleCommand (url: string, method: 'POST' | 'PUT' | 'DELETE', body?: any): Promise<AmorgAdminHttpCommandResult>
{
    const response = await fetch(
        url,
        {
            method,
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: body ? JSON.stringify(body) : undefined
        }
    );
    if (response.status === 401)
        onNoAccess();
    else 
    {
        const result = await response.json();
        return result as AmorgAdminHttpCommandResult;
    }
}

async function handleQuery<T> (url: string): Promise<T>
{
    const response = await fetch(
        url,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        }
    );
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
    {
        const result = await response.json();
        return result as T;
    }
    else
        throw new Error('Unhandled result');
}

async function getTypes (): Promise<ArticleTypeListing[]>
{
    const result = await handleQuery<ArticleTypeListing[]>(`${articlesPath}/types`);
    return result.map(t =>
    ({
        ...t,
        created: new Date(t.created)
    }));
}

async function createType (id: string, name: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/types`, 'POST',
        { id, name }
    );
}

async function updateType (id: string, name: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/types/${id}`, 'PUT',
        { name }
    );
}

async function deleteType (id: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/types/${id}`, 'DELETE');
}

async function getLists (): Promise<ArticleList[]>
{
    const result = await handleQuery<ArticleList[]>(`${articlesPath}/lists`);
    return result.map(l =>
    ({
        ...l,
        created: new Date(l.created),
        topics: l.topics.map(t =>
        ({
            ...t,
            created: new Date(t.created)
        }))
    }));

}

async function createList (id: string, name: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/lists`, 'POST',
        { id, name }
    );
}

async function updateList (id: string, name: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/lists/${id}`, 'PUT',
        { name }
    );
}

async function deleteList (id: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/lists/${id}`, 'DELETE');
}

async function createListTopic (listId, id: string, name: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/lists/${listId}/topics`, 'POST',
        { id, name }
    );
}

async function updateListTopic (id: string, name: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/topics/${id}`, 'PUT',
        { name }
    );
}

async function deleteListTopic (id: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/topics/${id}`, 'DELETE');
}

async function getArticleListing (page: number, pageSize: number, params?: ArticleParameters): Promise<ArticlePage>
{
    const urlParams: { key: string, value: string; }[] = [];

    if (params?.subscriberId)
        urlParams.push({ key: 'subid', value: params.subscriberId });

    urlParams.push({ key: 'sz', value: pageSize.toString() });

    if (params?.filter?.listId)
        urlParams.push({ key: 'lid', value: params.filter.listId });

    if (params?.filter?.topicId)
        urlParams.push({ key: 'toid', value: params.filter.topicId });

    if (params?.filter?.typeId)
        urlParams.push({ key: 'tyid', value: params.filter.typeId });

    if (params?.filter?.searchTerm)
        urlParams.push({ key: 'search', value: params.filter.searchTerm });

    if (params?.filter?.released === true)
        urlParams.push({ key: 'rel', value: '1' });
    else if (params?.filter?.released === false)
        urlParams.push({ key: 'rel', value: '0' });

    if (params?.sortBy)
        urlParams.push({ key: 'sort', value: params.sortBy });

    if (params?.sortOrder)
        urlParams.push({ key: 'order', value: params.sortOrder });

    const paramsString = urlParams.length === 0
        ? ''
        : `?${urlParams.map(p => `${p.key}=${p.value}`).join('&')}`;

    const resultPage = await handleQuery<ArticlePage>(`${articlesPath}/${page}${paramsString}`);
    resultPage.articles = resultPage.articles.map(a => ({
        ...a,
        date: a.date
            ? new Date(a.date)
            : null,
        release: a.release === null
            ? null
            : {
                ...a.release,
                releasedDate: new Date(a.release.releasedDate)
            },
        lastNote: a.lastNote
            ? {
                ...a.lastNote,
                date: new Date(a.lastNote.date)
            }
            : null
    }));

    return resultPage;
}

async function getArticleVersions (id: string): Promise<ArticleVersionList>
{
    const result = await handleQuery<ArticleVersionList>(`${articlesPath}/versions/${id}`);
    return {
        ...result,
        versions: result.versions.map(a => ({
            ...a,
            date: a.date
                ? new Date(a.date)
                : null,
            release: a.release === null
                ? null
                : {
                    ...a.release,
                    releasedDate: new Date(a.release.releasedDate)
                },
            notes: a.notes.map(n => ({
                ...n,
                created: new Date(n.created),
                lastEdited: new Date(n.lastEdited)
            })),
            appendices: a.appendices.map(ap => ({
                ...ap,
                date: ap.date ? new Date(ap.date) : null
            })),
        }))
    };
}

async function createArticle (articleId: string, articleVersionId: string, typeId: string, topicId: string, title: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/${articleId}`, 'POST',
        { articleVersionId, typeId, topicId, title },
    );
}

async function createArticleVersion (articleId: string, articleVersionId: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/${articleId}/versions`, 'POST',
        { articleVersionId }
    );
}

async function updateArticleField (articleId: string, change: ArticleFieldChange): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/${articleId}`, 'PUT',
        { change }
    );
}

async function createNote (articleVersionId: string, id: string, note: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/notes/`, 'POST',
        { id, articleVersionId, note }
    );
}

async function updateNote (id: string, note: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/notes/${id}`, 'PUT',
        { note }
    );
}

async function deleteNote (id: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/notes/${id}`, 'DELETE');
}

async function createAppendix (id: string, appendixId: string, articleVersionId: string, typeId: string, topicId: string, title: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/appendices`, 'POST',
        { id, appendixId, articleVersionId, typeId, topicId, title }
    );
}

async function updateAppendixField (id: string, change: AppendixFieldChange): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/appendices/${id}`, 'PUT',
        { change }
    );
}

async function deleteAppendix (id: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/appendices/${id}`, 'DELETE');
}

async function releaseArticleVersion (articleId: string, version: number, silentRelease:boolean): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/${articleId}/versions/${version}${silentRelease && '?silentRelease=1'}`, 'POST');
}

async function mergeUnreleasedArticleVersion (articleId: string, version: number): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(
        apiEndpoints.amorgy_articles_id_versions_version_merge
            .replace(':id', articleId)
            .replace(':version', version.toString()),
        'POST'
    );
}

async function updateReleaseRecord (id: string, customChangeDescription: string, customAssessment: string, customImportance: ArticleChangeImportance, customEmailTemplate: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/releaserecord/${id}`, 'PUT',
        { customChangeDescription, customAssessment, customImportance, customEmailTemplate }
    );
}


async function getSubscriberListings (): Promise<ArticleTopicSubscriberListing[]>
{
    return await handleQuery<ArticleTopicSubscriberListing[]>(`${articlesPath}/subscribers`);
}

async function createSubscriber (id: string, siteId: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/subscribers`, 'POST',
        { id, siteId }
    );
}

async function toggleArticleSubscription (subscriberId: string, articleId: string, active: boolean): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/subscribers/article/${articleId}`, 'PUT',
        { subscriberId, active }
    );
}


async function getFollowups (): Promise<ReleaseFollowUp[]>
{
    const followups = await handleQuery<ReleaseFollowUp[]>(`${articlesPath}/followups`);
    return followups;
}

async function getContacts (subscriberId?: string, articleId?: string): Promise<ArticleContact[]>
{
    const urlParams: { key: string, value: string; }[] = [];
    if (subscriberId)
        urlParams.push({ key: 'subid', value: subscriberId });
    if (articleId)
        urlParams.push({ key: 'artid', value: articleId });

    const paramsString = urlParams.length === 0
        ? ''
        : `?${urlParams.map(p => `${p.key}=${p.value}`).join('&')}`;

    const contacts = await handleQuery<ArticleContact[]>(`${articlesPath}/contacts${paramsString}`);
    return contacts;
}

async function getRetsinfoRecords(date:Date): Promise<RetsinfoHarvestRecord[]>
{

    const universalDate = date.getFullYear() + '-' + (date.getMonth()+1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');

    const data = await handleQuery<RetsinfoHarvestRecord[]>(`${articlesPath}/retsinfo/${universalDate}`);

    for(const record of data)
    {
        record.executedTimestamp = new Date(record.executedTimestamp);
        for(const change of record.changes)
        {
            change.changeData.changeDate = new Date(change.changeData.changeDate);
        }
    }
    return data;
}

async function fetchRetsinfoRecords(date:Date, refetch:boolean): Promise<AmorgAdminHttpCommandResult>
{
    const universalDate = date.getFullYear() + '-' + (date.getMonth()+1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');

    return await handleCommand(`${articlesPath}/retsinfo/${universalDate}${refetch ? '?refetch=1' : ''}`, 'POST');
}


async function fetchRetsinfoRecordChangeDoc(recordId:string, changeIndex:number): Promise<AmorgAdminHttpCommandResult>
{
    return await handleCommand(`${articlesPath}/retsinfo/${recordId}/changes/${changeIndex}`, 'POST');
}

async function updateRetsinfoRecordFlag(id: string, changeIndex: number, flagValue: string): Promise<AmorgAdminHttpCommandResult>
{
    return handleCommand(`${articlesPath}/retsinfo/${id}`, 'PUT',
        { changeIndex, flagValue }
    );
}